import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { diskussionService } from '../../services/diskussionService';
import type { DiskussionLinkSuggestion } from '../../types';

const GROUP_LABEL: Record<string, string> = {
  mitarbeiter: 'Kolleg:innen (Erwähnung)',
  audit: 'Audits',
  projekt: 'Projekte',
  schwachstelle: 'Schwachstellen',
  kunde: 'Kunden',
  tool: 'Tools',
};

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export interface DiskussionLinkPickerProps {
  onInsert: (snippet: string) => void;
  disabled?: boolean;
}

const DiskussionLinkPicker: React.FC<DiskussionLinkPickerProps> = ({ onInsert, disabled }) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DiskussionLinkSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const debounced = useDebouncedValue(inputValue, 320);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (debounced.trim().length < 1) {
      setOptions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    diskussionService
      .linkSuggestions(debounced.trim(), 20)
      .then((rows) => {
        if (!cancelled) setOptions(rows);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, open]);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Verlinkung einfügen
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
        <Chip
          size="small"
          label="Hall of Fame"
          disabled={disabled}
          onClick={() => onInsert('[[aio:hof_uebersicht:0]]')}
        />
      </Box>
      <Autocomplete<DiskussionLinkSuggestion, false, false, false>
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={null}
        inputValue={inputValue}
        onInputChange={(_, v) => setInputValue(v)}
        options={options}
        loading={loading}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.insert_text === b.insert_text}
        filterOptions={(x) => x}
        groupBy={(o) => o.group}
        renderGroup={(params) => (
          <li key={params.key}>
            <ListSubheader component="div" sx={{ bgcolor: 'background.paper', lineHeight: 2 }}>
              {GROUP_LABEL[params.group] ?? params.group}
            </ListSubheader>
            {params.children}
          </li>
        )}
        onChange={(_, v) => {
          if (v) {
            onInsert(v.insert_text);
            setInputValue('');
            setOpen(false);
          }
        }}
        noOptionsText={
          inputValue.trim().length < 1 ? 'Suchbegriff eingeben' : loading ? 'Suche…' : 'Keine Treffer'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Nutzer, Audit, Projekt …"
            placeholder="Tippen zum Suchen"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default DiskussionLinkPicker;
